/**
 * UI-mode store - the edit/test primitive + the screen-dim scalar.
 *
 * OPButtons has TWO UI states. `test` was named `play` until 2026-08-11
 * (operator: "so user doesn't get comfortable with play"):
 *
 *   - `edit`  - authoring. Palette, inspector, item drag, undo/redo, the
 *               Timeline/Matrix/Macros/Settings editors, page reorder.
 *   - `test`  - normal use. Every control fires; page switching;
 *               right-click-drag-anywhere (DEC-054) to nudge a control
 *               mid-set.
 *
 * There used to be a third, `performance`, which locked editing, collapsed
 * the header and needed a 2s hold to leave. RETIRED 2026-09-25 (DEC-216):
 * performance mode is now the ONE suite-wide switch OPBridge holds, and the
 * operator ruled it "shouldn't change view, just background stuff". It lives
 * in `opbridge/performanceMode.ts` and is not a UI state at all.
 *
 * Mode *transitions* that also touch layout selection live in
 * `layout/store.ts` (`toggleEditMode`) so this module stays free of a layout
 * import and there's no import cycle. `isEditMode` is re-exported from
 * `layout/store.ts` unchanged.
 */

import { createSignal } from 'solid-js';

export type UiMode = 'edit' | 'test';

// The single source of truth for which UI state we're in. Default 'test'
// — the app boots straight into normal use (a booted-in editor would
// invite accidental edits). Not persisted: mode is a per-session UI
// intent, not project data, exactly as the old `editMode` boolean was.
const [uiModeSig, setUiModeSig] = createSignal<UiMode>('test');
export const uiMode = uiModeSig;
export const setUiMode = setUiModeSig;

export const isEditMode = (): boolean => uiModeSig() === 'edit';
export const isTestMode = (): boolean => uiModeSig() === 'test';

// ─── Screen dim (was "stage dim", SD-4) ───────────────────────────
// Global brightness scalar 0.4-1.0 applied as `filter: brightness()` on the
// shell root whenever it is below 1. An ordinary Settings slider since
// 2026-09-25 (DEC-216); it used to apply only in performance mode, and the
// operator's rule is that performance mode changes no view. Machine truth -
// the iPad's dim level is a property of the room + device, never synced or
// persisted into the project. Kept in localStorage per-device.
//
// NEW KEY on purpose: the old `opbuttons.stageDim.v1` value was only ever
// applied in performance mode, so carrying it over would darken a screen that
// has always rendered at full brightness in normal use. Starts at 100%.
const STAGE_DIM_KEY = 'opbuttons.screenDim.v2';
export const STAGE_DIM_MIN = 0.4;
export const STAGE_DIM_MAX = 1.0;

export function clampStageDim(n: number): number {
  if (!Number.isFinite(n)) return STAGE_DIM_MAX;
  return Math.max(STAGE_DIM_MIN, Math.min(STAGE_DIM_MAX, n));
}

function loadStageDim(): number {
  if (typeof localStorage === 'undefined') return STAGE_DIM_MAX;
  const raw = localStorage.getItem(STAGE_DIM_KEY);
  if (raw === null) return STAGE_DIM_MAX;
  const n = parseFloat(raw);
  return clampStageDim(n);
}

const [stageDimSig, setStageDimSig] = createSignal(loadStageDim());
export const stageDim = stageDimSig;

export function setStageDim(n: number): void {
  const v = clampStageDim(n);
  setStageDimSig(v);
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(STAGE_DIM_KEY, String(v));
    } catch {
      // Private-mode / quota — dim just won't persist across reloads.
    }
  }
}

// Continuous-control guard (SD-5): long-press this long to "grab" a
// guarded fader / XY pad; a quick touch shorter than this sends nothing.
// Moving more than GUARD_GRAB_MOVE_TOL px before the grab lands cancels
// it (it was a swipe, not a deliberate press-to-grab).
export const GUARD_GRAB_HOLD_MS = 400;
export const GUARD_GRAB_MOVE_TOL = 10;

// Discrete-control guard (SD-5): first tap ARMS the control for this long,
// a second tap within the window fires; timeout disarms silently. Used by
// buttons (FireButton) and the composite selector items (grids, transpose/
// capo/waves) via `createDiscreteGuard`. Matches the 2s exit-hold cadence.
export const GUARD_ARM_MS = 2000;
